import useSWR              from "swr"
import { useEffect }       from "react"
import { useAuth as useClerkAuth } from "@clerk/react"
import { api }             from "@/lib/api-client"
import type { AuthUser }   from "@/lib/api-client"
import { identifyUser }    from "@/lib/observability"
import { isDoctorHost }    from "@/lib/host"

export function useAuth() {
  const { isSignedIn, isLoaded } = useClerkAuth()

  // Only fetch clinic DB user data when:
  //   - Clerk session is loaded + signed in
  //   - Not on the D2C host (summarisenow) — no clinic session exists there
  const shouldFetch = isLoaded && isSignedIn && !isDoctorHost()

  const { data, error, isLoading, mutate } = useSWR<AuthUser | null>(
    shouldFetch ? "auth/me" : null,
    () => api.auth.me().catch(() => null),
    { revalidateOnFocus: false }
  )

  useEffect(() => {
    if (data?.id) {
      identifyUser({
        id:       String(data.id),
        email:    (data as unknown as { email?: string }).email,
        tenantId: (data as unknown as { tenantId?: string }).tenantId,
        role:     data.role,
      })
    }
  }, [data?.id])

  const role = data?.role
  return {
    user:        data ?? null,
    loading:     !isLoaded || isLoading,
    error,
    isSignedIn:  Boolean(isSignedIn),
    isAdmin:     role === "admin" || data?.isPlatformAdmin === true,
    isOwner:     role === "owner" || role === "admin" || data?.isPlatformAdmin === true,
    isDeveloper: role === "developer" || role === "owner" || role === "admin" || data?.isPlatformAdmin === true,
    role,
    refresh: mutate,
  }
}
